 import { Card, Button, Badge, Skeleton } from "@dawa/ui";
  import { CopyButton } from "./CopyButton";
  import { Tags } from "lucide-react";

  function lengthBadge(len: number, min: number, max: number) {
  let cls = "bg-muted/20 text-text";

  if (len >= min && len <= max) {
    cls = "bg-success text-bg";
  } else if ((len > 0 && len < min) || len > max) {
    cls = "bg-warning text-bg";
  }

  return (
    <span className={`rounded px-2 py-0.5 text-xs ${cls}`}>
      {len} chars
    </span>
  );
}

  export function MetaCard({
  meta,
  loading = false,
  onGenerate,
  }: {
  meta: any;
  loading?: boolean;
  onGenerate?: () => void;
  }) {
  const title = meta?.title ?? meta?.seo_title ?? "";
  const description = meta?.description ?? meta?.seo_description ?? "";
  const keywords: string[] = Array.isArray(meta?.keywords)
      ? meta.keywords
      : typeof meta?.keywords === "string"
        ? meta.keywords.split(",").map((x: string) => x.trim()).filter(Boolean)
        : [];

  return (
      <Card className="space-y-4 p-4">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <Tags className="h-4 w-4 text-muted" /> Meta
          </h2>
          <div className="flex gap-2">
            {title && <CopyButton text={title} label="Copier titre" />}
            {description && <CopyButton text={description} label="Copier description" />}
            {meta && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                aria-label="Exporter metas JSON"
                onClick={() => {
                  try {
                    const json = JSON.stringify(meta, null, 2);
                    const blob = new Blob([json], { type: "application/json;charset=utf-8" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    const base = (meta?.slug || title || "meta").toString().toLowerCase().replace(/[^a-z0-9-_]/g, "-");
                    a.href = url;
                    a.download = `${base}-meta.json`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                  } catch {
                    /* ignore */
                  }
                }}
              >
                Exporter JSON
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-1">
          <div className="text-xs text-muted">Title</div>
          {loading ? (
            <Skeleton className="max-w-xl" />
          ) : (
            <div className="flex items-center justify-between gap-2">
              <div className="font-medium truncate" title={title}>{title || "—"}</div>
              {title && lengthBadge(title.length, 50, 60)}
            </div>
          )}
        </div>

        <div className="space-y-1">
          <div className="text-xs text-muted">Description</div>
          {loading ? (
            <Skeleton className="max-w-2xl" lines={3} />
          ) : (
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm whitespace-pre-wrap break-words flex-1">{description || "—"}</p>
              {description && lengthBadge(description.length, 140, 160)}
            </div>
          )}
        </div>

        {!loading && keywords.length > 0 && (
          <div className="space-y-1">
            <div className="text-xs text-muted">Keywords</div>
            <div className="flex flex-wrap gap-1">
              {keywords.map((k, i) => (
                <Badge key={i} variant="secondary" className="text-xs">{k}</Badge>
              ))}
            </div>
          </div>
        )}

        {!loading && !title && !description && keywords.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-3 py-4 text-center">
            <div className="text-sm text-muted">Aucune meta générée pour l’instant</div>
            {onGenerate && (
              <Button type="button" onClick={onGenerate}>
                Générer les metas
              </Button>
            )}
          </div>
        )}
      </Card>
  );
  }

